import React from 'react';
import { observer } from 'mobx-react';
import cx from 'classnames';
import { Navigate } from 'react-router-dom';
import { TafCheckboxInput, TafButton } from '@taf-ui/react';
import { TafChangeEvent } from '@taf-ui/core';
import AppStorage from 'Storage/Storage';
import { isBrand, translate } from 'utils/utils';
import DraftService from 'Requests/DraftService/DraftService';
import FinalizeService from 'Requests/FinalizeService/FinalizeService';
import Steps from 'enums/Steps';
import Labels from 'enums/Labels';
import { Loader } from 'Loading/Loading';
import HealthDeclarationStatus from 'enums/HealthDeclarationStatus';
import RouteParams from '../interfaces/RouteParams';
import CompleteState from '../interfaces/Complete';
import Brand from '../enums/Brand';
import { languageToReadSpeakerLang } from 'ReadSpeakerReader/ReadSpeakerProvider';
import ReadSpeakerReader from 'ReadSpeakerReader';

class CompleteDHMR extends React.PureComponent<RouteParams, CompleteState> {
  private readonly storage: AppStorage;

  private readonly draftService: DraftService;

  private readonly finalizeQSService: FinalizeService;

  private pathOfInvalidPage: string | null;

  constructor(props: RouteParams) {
    super(props);
    this.storage = AppStorage.getInstance();
    this.draftService = new DraftService();
    this.finalizeQSService = new FinalizeService();
    this.pathOfInvalidPage = null;
    this.state = {
      isFinalizing: false,
      navigateComponent: undefined,
    };
  }

  componentDidMount(): void {
    this.storage.modifyProgressBar(Steps.All, 'active', false);
    this.storage.modifyProgressBar(Steps.Complete, 'active', true);
    this.checkIfWeNeedToStoreDraft();
  }

  componentWillUnmount(): void {
    this.storage.modifyProgressBar(Steps.Complete, 'done', true);
    this.storage.modifyProgressBar(Steps.Complete, 'active', false);
  }

  checkIfWeNeedToStoreDraft = (): void => {
    const currentHDStatus = this.storage.getCurrentHDStatus();
    const { isFinalizing } = this.state;
    if (
      currentHDStatus === HealthDeclarationStatus.IN_PROGRESS &&
      !isFinalizing
    ) {
      this.draftService.syncLater(500);
    }
  };

  handleAgreementOfPersonalInfo = (
    event: CustomEvent<TafChangeEvent<boolean>>,
  ): void => {
    this.storage.setIsAgreedWithProcessingPersonalInfo(event.detail.value);
    this.checkIfPageValid();
    this.draftService.saveDraft();
  };

  handleAgreementOfPrivacyInfo = (
    event: CustomEvent<TafChangeEvent<boolean>>,
  ): void => {
    this.storage.setIsAgreedWithPrivacyInfo(event.detail.value);
    this.checkIfPageValid();
    this.draftService.saveDraft();
  };

  handleAgreementOfMedicalInfo = (
    event: CustomEvent<TafChangeEvent<boolean>>,
  ): void => {
    this.storage.setIsAgreedWithProcessingMedicalInfo(event.detail.value);
    this.checkIfPageValid();
    this.draftService.saveDraft();
  };

  handleCorrectnessOfMobilePhone = (
    event: CustomEvent<TafChangeEvent<boolean>>,
  ): void => {
    this.storage.setIsMobilePhoneCorrect(event.detail.value);
    this.checkIfPageValid();
    this.draftService.saveDraft();
  };

  checkIfPageValid = (): void => {
    this.storage.modifyProgressBar(
      Steps.Complete,
      'valid',
      this.getIsPageValid(),
    );
  };

  getIsPageValid = (): boolean =>
    this.storage.getIsAgreedWithProcessingPersonalInfo &&
    this.storage.getIsAgreedWithProcessingMedicalInfo &&
    this.storage.getIsMobilePhoneCorrect;

  checkPreviousStepsValidity = (): boolean =>
    this.storage
      .getProgressBarByUnderwriterOwner()
      .filter((step) => step.step !== Steps.Complete)
      .every((step) => {
        if (!step.valid) {
          this.setPathOfInvalidPage(step.path);
        }
        return step.valid;
      });

  setPathOfInvalidPage = (path: string | null): void => {
    this.pathOfInvalidPage = path;
  };

  proceedToSigning = (): void => {
    this.setPathOfInvalidPage(null);
    this.storage.modifyProgressBar(Steps.Complete, 'done', true);
    const isWholeAppValid =
      this.checkPreviousStepsValidity() && this.getIsPageValid();
    if (isWholeAppValid) {
      this.finalizeQS();
    } else {
      this.redirectToInvalidPage();
    }
  };

  redirectToInvalidPage = (): void => {
    if (this.pathOfInvalidPage) {
      const { healthDeclarationId } = this.props;
      this.setState({
        navigateComponent: (
          <Navigate
            to={`/${healthDeclarationId}/${this.pathOfInvalidPage}`}
            replace={true}
          />
        ),
      });
    }
  };

  setIsFinalizing = (isFinalizing: boolean): void => {
    this.setState({
      isFinalizing,
    });
  };

  finalizeQS(): void {
    this.setIsFinalizing(true);
    this.storage.setIsProgressBarHidden(true);
    this.finalizeQSService
      .finalize()
      .then((response) => {
        this.setIsFinalizing(false);
        this.storage.setIsProgressBarHidden(false);
        window.location.href = response.data.result.signUrl || '';
      })
      .catch(() => {
        const { healthDeclarationId } = this.props;
        this.setState({
          navigateComponent: (
            <Navigate to={`/${healthDeclarationId}/error`} replace={true} />
          ),
        });
      });
  }

  render(): React.ReactElement {
    const {
      phonePrefix = '',
      phoneNumberBody = '',
      surname = '',
      initials = '',
      firstName = '',
    } = this.storage.getPersonalDataFormState;

    let { middleName } = this.storage.getPersonalDataFormState;

    const productSpecificData = this.storage.getProductSpecificInfo;
    const productSpecificProgressBarLabels =
      this.storage.getProductSpecificProgressBarLabels || [];

    if (!middleName) {
      middleName = '';
    }

    const { isFinalizing, navigateComponent } = this.state;

    if (navigateComponent) {
      return <>{navigateComponent}</>;
    }

    const lang = this.storage.getLocale;

    const phone = `${phonePrefix}${phoneNumberBody}`;

    if (!this.storage.getToken || isFinalizing) {
      return <Loader />;
    }

    const fullName = isBrand(Brand.wefox)
      ? `${firstName} ${middleName} ${surname}`
      : `${initials} ${middleName} ${surname}`;

    return (
      <div
        className="completeScreen"
        id="complete-screen"
        lang={languageToReadSpeakerLang(lang)}
      >
        <ReadSpeakerReader id="complete-screen" />
        <h1>{translate(Labels.COMPLETE_DHMR_HEADER, lang)}</h1>
        <p>{translate(Labels.COMPLETE_DHMR_P1, lang)}</p>

        <p>
          <span>{`${translate(Labels.COMPLETE_P3, lang)}, `}</span>
          <span className="bold">{`${fullName}, `}</span>
          <span>{`${translate(Labels.COMPLETE_P4, lang)}`}</span>
        </p>

        <ul className="checkList">
          <li>{translate(Labels.COMPLETE_DHMR_LI1, lang)}</li>
          <li>{translate(Labels.COMPLETE_DHMR_LI2, lang)}</li>
        </ul>
        <div
          className={cx('checkbox-wrapper', {
            'error-wrapper':
              !this.storage.getIsAgreedWithPrivacyInfo &&
              this.storage.getActiveStep?.done,
          })}
        >
          <TafCheckboxInput
            value={this.storage.getIsAgreedWithPrivacyInfo}
            name="isAgreedWithProcessingPersonalInfo"
            inputId="isAgreedWithProcessingPersonalInfo"
            label={translate(Labels.COMPLETE_DHMR_LI3, lang, [
              'TAF',
              productSpecificData.privacyStatementLink,
              productSpecificData.privacyStatementLink,
              'TAF',
            ])}
            onTafChange={this.handleAgreementOfPrivacyInfo}
          />
        </div>
        <div
          className={cx('checkbox-wrapper', {
            'error-wrapper':
              !this.storage.getIsAgreedWithProcessingPersonalInfo &&
              this.storage.getActiveStep?.done,
          })}
        >
          <TafCheckboxInput
            value={this.storage.getIsAgreedWithProcessingPersonalInfo}
            name="isAgreedWithProcessingPersonalInfo"
            inputId="isAgreedWithProcessingPersonalInfo"
            label={translate(Labels.COMPLETE_CHECKBOX_TITLE1, lang, ['TAF'])}
            onTafChange={this.handleAgreementOfPersonalInfo}
          />
        </div>
        <div
          className={cx('checkbox-wrapper', {
            'error-wrapper':
              !this.storage.getIsAgreedWithProcessingMedicalInfo &&
              this.storage.getActiveStep?.done,
          })}
        >
          <TafCheckboxInput
            value={this.storage.getIsAgreedWithProcessingMedicalInfo}
            name="isAgreedWithProcessingMedicalInfo"
            inputId="isAgreedWithProcessingMedicalInfo"
            label={translate(Labels.COMPLETE_CHECKBOX_TITLE2, lang)}
            onTafChange={this.handleAgreementOfMedicalInfo}
          />
        </div>
        <div
          className={cx('checkbox-wrapper', {
            'error-wrapper':
              !this.storage.getIsMobilePhoneCorrect &&
              this.storage.getActiveStep?.done,
          })}
        >
          <TafCheckboxInput
            value={this.storage.getIsMobilePhoneCorrect}
            name="isMobilePhoneCorrect"
            inputId="isMobilePhoneCorrect"
            label={`${translate(Labels.COMPLETE_CHECKBOX_TITLE3, lang)} <span style="font-weight:bold">${phone}</span>`}
            onTafChange={this.handleCorrectnessOfMobilePhone}
          />
        </div>
        <p>
          {translate(Labels.COMPLETE_DHMR_P5, lang, [
            (productSpecificProgressBarLabels[1] || '').toUpperCase(),
          ])}
        </p>
        <p>{translate(Labels.COMPLETE_P7, lang)}</p>
        <div className="signingBtn">
          <TafButton
            id="goToSigning"
            onClick={this.proceedToSigning}
            type="button"
            primary={true}
            disabled={isFinalizing}
            title={translate(Labels.PROCEED_TO_SIGNING, lang)}
          >
            {translate(Labels.PROCEED_TO_SIGNING, lang)}
          </TafButton>
        </div>
      </div>
    );
  }
}

export default observer(CompleteDHMR);
